<script setup lang="ts">
	import MzListAction from '@/components/MzListAction.vue';
	import AccessHub from '@/components/AccessHub.vue';
	import { MzIcon } from '@monizze/monizze-components';
	import { PhWarningDiamond } from '@phosphor-icons/vue';
	import { useI18n } from 'vue-i18n';
	import { useBrowserLocation } from '@vueuse/core';
	import { useUserStore } from '@/stores/user.ts';
	import { useAuthStore } from '@/stores/auth.ts';
	import { useRoute, useRouter } from 'vue-router';
	import { computed, onMounted, ref } from 'vue';
	import { callDiscoverableApp } from '@/services/discoverable_apps';
	import { DiscoveredApp } from '@/types/Responses/App.ts';
	import { useMerchantAreaTile } from '@/composables/useMerchantAreaTile.ts';

	const { t } = useI18n();
	const fullURL: string = useBrowserLocation().value.href || '';
	const url: URL = new URL(fullURL);
	const domain: string = `${url.protocol}//${url.hostname}`;
	const userStore = useUserStore();
	const authStore = useAuthStore();
	const route = useRoute();
	const router = useRouter();
	const { showMerchantAreaTile } = useMerchantAreaTile();

	if (typeof route.query.redirect_uri === 'string') {
		window.location.href = route.query.redirect_uri;
	}
	const appTiles = ref<DiscoveredApp[]>([]);
	const hasPartnerConfigError = computed(() => route.query.error === 'partner-config');

	const loginWithOtherCredentials = async () => {
		await authStore.logout();
		await router.push('/');
	};

	onMounted(async () => {
		const discoverableApps = import.meta.env.VITE_DISCOVERABLE_APPS.split(',');
		await Promise.all(
			discoverableApps.map(async (appUrl: string) => {
				const discovered = await callDiscoverableApp(appUrl);
				appTiles.value.push(...discovered);
			}),
		);
	});

	const partnerAreaDomain = computed(() => {
		if (userStore.user.partner_user_id) {
			return authStore.isLux ? 'partner.up' : 'partner';
		}

		return '';
	});

	const initialsFor = (name: string): string => {
		const segments = name
			.replace(/([a-z])([A-Z])/g, '$1 $2')
			.split(/\s+/)
			.filter(Boolean);
		if (segments.length >= 2) {
			return segments[0][0].toUpperCase() + segments[1][0].toLowerCase();
		}
		const first = segments[0] ?? '';
		return (first[0] ?? '').toUpperCase() + (first[1] ?? '').toLowerCase();
	};
</script>

<template>
	<access-hub>
		<div
			v-if="hasPartnerConfigError"
			class="flex flex-wrap items-start gap-4 rounded bg-bg-feedback-error border border-border-feedback-error p-4"
		>
			<MzIcon :icon="PhWarningDiamond" class="text-feedback-error text-xl" />
			<div class="flex flex-1 flex-col gap-4">
				<p>{{ t('partner.page.invalid-config.message') }}</p>
				<button type="button" class="w-fit font-bold underline" @click="loginWithOtherCredentials">
					{{ t('partner.page.invalid-config.relogin') }}
				</button>
			</div>
		</div>
		<div class="flex flex-col divide-y divide-border-default">
			<mz-list-action
				v-if="partnerAreaDomain"
				:href="domain.replace('account', partnerAreaDomain)"
				:title="t('partner.page.partner-area')"
				:initials="initialsFor(t('partner.page.partner-area'))"
				variant="minimal"
			/>
			<mz-list-action
				v-if="userStore.user.customer_user_id"
				:href="domain.replace('account', 'client')"
				:title="t('partner.page.client-area')"
				:initials="initialsFor(t('partner.page.client-area'))"
				variant="minimal"
			/>
			<mz-list-action
				v-if="showMerchantAreaTile"
				:href="domain.replace('account', 'merchant')"
				:title="t('partner.page.merchant-area')"
				:initials="initialsFor(t('partner.page.merchant-area'))"
				variant="minimal"
			/>
			<mz-list-action
				v-if="userStore.user.user_id"
				:href="domain.replace('account', 'my')"
				:title="t(authStore.isLux ? 'partner.page.my-up' : 'partner.page.my-monizze')"
				:initials="initialsFor(t(authStore.isLux ? 'partner.page.my-up' : 'partner.page.my-monizze'))"
				variant="minimal"
			/>
			<mz-list-action
				v-if="userStore.user.fridom_user_id"
				:href="domain.replace('account', 'fridom')"
				:title="t('partner.page.fridom')"
				:initials="initialsFor(t('partner.page.fridom'))"
				variant="minimal"
			/>
			<mz-list-action
				v-if="!authStore.isLux"
				href="https://account.monizze.be/partner?client_id=17&redirect_uri=https://dealzz.izify.com/callback/login/monizze&state=%26routePath%3Dhome.index"
				:title="t('partner.page.dealzz')"
				:alt="t('partner.page.dealzz')"
				image="/Partner/dealzz.svg"
				variant="minimal"
			/>
			<mz-list-action
				v-for="app in appTiles"
				:key="app.url"
				:href="app.url"
				:title="app.name"
				:initials="initialsFor(app.name)"
				variant="minimal"
			/>
		</div>
	</access-hub>
</template>
